// Pure sizing for the small charts. See Design > UI, "Curator Studio assets".

/** Whole percentages that always sum to 100 (largest remainder), for labels. A zero total gives zeros. */
export function percentShares(values: ReadonlyArray<number>): number[] {
  const clean = values.map(value => (Number.isFinite(value) && value > 0 ? value : 0));
  const total = clean.reduce((sum, value) => sum + value, 0);
  if (total === 0) return clean.map(() => 0);
  const exact = clean.map(value => (value / total) * 100);
  const shares = exact.map(Math.floor);
  const order = exact.map((value, index) => ({ index, rest: value - Math.floor(value) })).sort((a, b) => b.rest - a.rest || a.index - b.index);
  let missing = 100 - shares.reduce((sum, value) => sum + value, 0);
  for (const { index } of order) {
    if (missing <= 0) break;
    shares[index] = (shares[index] ?? 0) + 1;
    missing -= 1;
  }
  return shares;
}

/** Bar widths as percentages of the largest value; a non-zero value never drops below `min` so it stays visible. */
export function barWidths(values: ReadonlyArray<number>, min = 2): number[] {
  const max = Math.max(0, ...values.filter(Number.isFinite));
  if (max === 0) return values.map(() => 0);
  return values.map(value => (Number.isFinite(value) && value > 0 ? Math.max(min, (value / max) * 100) : 0));
}

/** Flex weights for a breakdown: each part's share of the total, raised to `min` so a small or zero part keeps room for its label. */
export function breakdownWeights(values: ReadonlyArray<number>, min = 0.18): number[] {
  const clean = values.map(value => (Number.isFinite(value) && value > 0 ? value : 0));
  const total = clean.reduce((sum, value) => sum + value, 0);
  if (total === 0) return clean.map(() => 1);
  return clean.map(value => Math.max(min, value / total));
}
